import cx from "classnames";
import { useState } from "react";

import { appstr } from "@coreapp/utils/appstr";
import { Button, Flex, Text } from "@theorgchartdev/storybook";
import { PlanningChangeDisplayRow } from "../PlanningChangeDisplayRow";
import type { QChange } from "@coreapp/CoreApp/QChange";
import type { QObjDBObject } from "@coreapp/CoreApp/QObjDBObject";

const formatChangeFooter = (change: QChange | null): string | null => {
  const datetime = change?.GetFormattedChangeDateTime?.();
  const author = change?.fChangeAuthor;
  if (datetime && author) return appstr("change by author", { changeDatetime: datetime, author });
  if (datetime) return datetime;
  if (author) return appstr("by") + " " + author;
  return null;
};

type ChangeProps = {
  changes: QChange[];
  title: string;
  override?: never;
};

type OverrideProps = {
  override: QObjDBObject;
  title?: never;
  changes?: never;
};

type Props = (ChangeProps | OverrideProps) & {
  datatestPrefix: string;
  allChanges?: QChange[];
};

export const ChangesItemDisplay = (props: Props) => {
  const [showAllChanges, setShowAllChanges] = useState(false);
  const [isFirstCommentOverflowed, setIsFirstCommentOverflowed] = useState(false);

  const toggleShowMore = () => {
    setShowAllChanges(!showAllChanges);
  };

  const { changes, mostRecentChange, mostRecentChangeIdx } = getDisplayData(props);

  const hasMoreChangesToShow = changes?.length > 1 || isFirstCommentOverflowed;
  const recentFooter = formatChangeFooter(mostRecentChange);

  return (
    <Flex direction={"column"} gap={"4px"} width={"100%"} height={"100%"} alignItems={"flex-start"}>
      {showAllChanges ? (
        <Flex
          overflow={"auto"}
          className={cx("oc-list-container", "ocn-list-scrollbar")}
          direction={"column"}
          gap={"20px"}
        >
          {changes.map((change, index) => {
            const footer = formatChangeFooter(change);
            return (
              <Flex gap={"8px"} direction={"column"} className={"change-row-container"} key={index}>
                <Flex direction={"column"} gap={"8px"} style={{ paddingLeft: "12px" }}>
                  <PlanningChangeDisplayRow
                    change={change}
                    showCompact={false}
                    onOverflow={undefined}
                    showCommentBubble={index === 0}
                    changeIndex={index}
                    allChanges={props.allChanges}
                  />
                  {footer && (
                    <Text size={"xsmall"} colorModifier={"lightgrey"}>
                      {footer}
                    </Text>
                  )}
                </Flex>
              </Flex>
            );
          })}
        </Flex>
      ) : (
        <Flex gap={"8px"} direction={"column"} className={"change-row-container"}>
          <Flex direction={"column"} gap={"8px"} style={{ paddingLeft: "12px" }}>
            <PlanningChangeDisplayRow
              showCompact={true}
              onOverflow={setIsFirstCommentOverflowed}
              change={mostRecentChange}
              showCommentBubble={true}
              changeIndex={mostRecentChangeIdx}
              allChanges={props.allChanges}
            />
            {recentFooter && (
              <Text size={"xsmall"} colorModifier={"lightgrey"}>
                {recentFooter}
              </Text>
            )}
          </Flex>
        </Flex>
      )}
      {hasMoreChangesToShow && (
        <Button
          dataTestId={`${props.datatestPrefix}-more-toggle`}
          onClick={toggleShowMore}
          modifier={"tertiary"}
          size={"small"}
          iconProps={{ name: showAllChanges ? "square-minus" : "square-plus" }}
        >
          {showAllChanges ? appstr("Show less") : appstr("Show all")}
        </Button>
      )}
    </Flex>
  );
};

export const getDisplayData = (props: Props) => {
  let changes: QChange[] = [];
  let mostRecentChange: QChange | null = null;
  let mostRecentChangeIdx = -1;

  if (props.override) {
    changes = [...(props.override.fChanges ?? [])].reverse();

    const mostRecentPayload = props.override.lastActiveChange(true);
    mostRecentChange = mostRecentPayload.mostRecentChange;
    mostRecentChangeIdx = mostRecentPayload.index;
  } else {
    mostRecentChange = props.changes[0] || null;
    mostRecentChangeIdx = 0;
    changes = props.changes;
  }

  return {
    mostRecentChange,
    mostRecentChangeIdx,
    changes
  };
};
